/**
 * Modal to add a schedule group or subgroup by name.
 */
import { useEffect, useMemo, useState } from "react";
import { ModalWrapper } from "../../../../components/ui";
import { Button } from "../../../../components/ui";

/**
 * Modal form for creating a schedule group or subgroup by name.
 *
 * @param {Object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {(payload: { name: string }) => void} props.onSubmit
 * @returns {JSX.Element}
 */
export default function ScheduleCreateGroupModal({
  open,
  onClose,
  onSubmit,
}) {
  const [name, setName] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) {
      return;
    }
    setName("");
    setError("");
  }, [open]);

  /**
   * Whether the trimmed group name is non-empty.
   *
   * @param {void}
   * @returns {boolean}
   */
  const canSubmit = useMemo(() => Boolean(name.trim()), [name]);

  /**
   * Validate the name and submit a create payload to the parent.
   *
   * @param {import('react').FormEvent|import('react').MouseEvent} e Form or button event.
   * @returns {void}
   */
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim()) {
      setError("Group name is required.");
      return;
    }
    onSubmit({ name: name.trim() });
  };

  return (
    <ModalWrapper isOpen={open} onClose={onClose} closeButton={false} maxWidth={400}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="schedule-create-group-title"
      >
        <div className="border-b border-gray-200 px-4 py-3">
          <h2
            id="schedule-create-group-title"
            className="text-base font-semibold text-gray-900"
          >
            Add Group
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 px-4 py-4">
          <div>
            <label
              htmlFor="schedule-group-name"
              className="block text-sm font-medium text-gray-700"
            >
              Group name
            </label>
            <input
              id="schedule-group-name"
              type="text"
              className="mt-1 w-full rounded border border-gray-300 px-2 py-2 text-sm"
              placeholder="e.g., Site A – Main Building"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          {error ? (
            <p className="text-sm text-red-600" role="alert">
              {error}
            </p>
          ) : null}

          <div className="flex flex-wrap items-center justify-end gap-2 border-t border-gray-100 pt-4">
            <Button
              variant="custom"
              className="border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-800 hover:bg-gray-100"
              onClick={onClose}
              text="Cancel"
            />
            <Button
              variant="custom"
              className="bg-gray-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-gray-800"
              disabled={!canSubmit}
              onClick={handleSubmit}
              text="Save"
            />
          </div>
        </form>
      </div>
    </ModalWrapper>
  );
}

